import { Theme } from '@mui/material/styles';
import { pathOr, stringToPath } from 'remeda';

export const capitalizeString = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Make JSON-like string properly formatted to be consumable
 * by JSON.parse().
 *
 * Example: `{ name: 'John', 'age': 30 }` -> `{ "name": "John", "age": 30 }`
 */
export function sanitizeJSON(input: string): string {
  try {
    const sanitized = input
      .replace(/'/g, '"')
      .replace(/([{,]\s*)([A-Za-z0-9_]+)\s*:/g, '$1"$2":');
    return sanitized;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(`Error sanitizing JSON: ${error.message}`);
      throw error;
    }
    return input;
  }
}

/**
 * Coloring logic brought from Day Zero.
 */
export function hashCode(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

export function pickColor(str = '', saturation = 25, lightness = 50) {
  return `hsl(${hashCode(str) % 360}, ${saturation}%, ${lightness}%)`;
}

export function pickAvatarColor(str = '') {
  return pickColor(str, 50, 55);
}

/*
The constraints used for Pastel colors are:
Hue Full Range
Saturation 30% - 70%
Lightness 70% - 90%
*/
export function pickPastelColor(str = '', mode = 'light') {
  const randomSaturation = parseInt((hashCode(str) | 0).toString().substring(4, 6));
  const pastelSaturation =
    mode === 'dark'
      ? Math.min(Math.max(randomSaturation, 10), 30)
      : Math.min(Math.max(randomSaturation, 30), 70);

  const randomLightness = parseInt((hashCode(str) | 0).toString().substring(1, 3));
  const pastelLightness =
    mode === 'dark'
      ? Math.min(Math.max(randomLightness, 0), 20)
      : Math.min(Math.max(randomLightness, 70, 90));

  return `hsl(${hashCode(str) % 360}, ${pastelSaturation}%, ${pastelLightness}%)`;
}

export function slugify(str = '') {
  return str
    .replace(/^\s+|\s+$/g, '') // trim leading/trailing white space
    .toLowerCase() // convert string to lowercase
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '_') // replace spaces with underscores
    .replace(/-+/g, '-'); // remove consecutive hyphens
}

// this way we can easily handle both boolean values and strings like "yes" | "no" | "true" | "false"
const BOOL_TRUE_STRINGS = ['true', 'yes'];

export function isStringBooleanTrue(value?: string | boolean) {
  return BOOL_TRUE_STRINGS.includes(String(value)?.toLowerCase());
}

export function getThemeColor(path: string, theme: Theme): string {
  const propertyPath = stringToPath(path);

  return pathOr(theme.palette, propertyPath, 'white') as string;
}

export function humanize(str: string): string {
  return str
    .replace(/^[\s_]+|[\s_]+$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/([A-Z]+)/g, (m) => ` ${m}`)
    .replace(/\s([a-z])/g, (m) => m.toUpperCase())
    .replace(/^[a-z]/, (m) => m.toUpperCase())
    .trim();
}

export function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const toLowerCaseWords = (str: string): string[] =>
  str
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/** True when every word of `query` starts some word of `text`, in any order. */
export function matchesWordStarts(text: string, query: string): boolean {
  const queryWords = toLowerCaseWords(query);
  if (!queryWords.length) {
    return false;
  }

  const textWords = toLowerCaseWords(text);
  return queryWords.every((queryWord) => textWords.some((word) => word.startsWith(queryWord)));
}
